import { useEffect, useRef } from 'react';
import { useFilters } from '../../FilterContext';

/**
 * Custom hook for handling filter initialization with URL and localStorage persistence
 * 
 * @param {string} filterKey - The filter key (e.g., 'model', 'department', 'timeframe')
 * @param {Array} dataSource - The array of options/items for this filter
 * @param {Object} config - Configuration object
 * @param {Function} config.getDefaultValue - Function that returns the default value when no URL or localStorage value exists
 * @param {Function} config.getParams - Function that extracts API parameters from the selected item
 * @param {Function} config.findItem - Optional custom function to find item by ID (defaults to generic ID matching)
 * @param {Array} config.dependencies - Array of values that should trigger re-initialization when changed
 * @param {boolean} config.resetOnDependencyChange - Whether to reset when dependencies change
 * @param {boolean} config.waitForData - Whether to wait for dataSource to have items before initializing
 * @param {boolean} config.persistAll - Whether to persist 'all' values to localStorage/URL when Show All is selected
 */
export const useFilterInitialization = (filterKey, dataSource, config = {}) => {
  const {
    getDefaultValue = () => ({}),
    getParams = (item) => ({ [`${filterKey}Id`]: item.id }),
    findItem = null, // Will use generic findItemById if not provided
    dependencies = [],
    resetOnDependencyChange = false,
    waitForData = true,
    persistAll = false,
    multiSelect = false
  } = config;

  const { filterValues, updateFilter, getInitialFilterValue, initialValues, storageKey, siteAppId } = useFilters();
  const hasInitializedRef = useRef(false);
  const lastDependenciesRef = useRef(dependencies);
  const hasUrlValueRef = useRef(false);

  // Check if this filter has a URL value that should be preserved
  hasUrlValueRef.current = !!initialValues[filterKey];

  // Generic item finder that handles string/number ID matching
  const findItemById = (items, id) => {
    return items.find(item => 
      item.id === id || 
      item.id === parseInt(id) || 
      String(item.id) === String(id)
    );
  };

  const findItemInDataSource = findItem || findItemById;

  // Check if dependencies have changed (but only after initial setup AND not during URL/localStorage initialization)
  const dependenciesChanged = hasInitializedRef.current && 
                             !hasUrlValueRef.current && // Don't reset if this filter has a URL value to preserve
                             dependencies.some((dep, index) => 
                               lastDependenciesRef.current[index] !== dep
                             );

  // Reset initialization when dependencies change (but not on initial load)
  useEffect(() => {
    if (resetOnDependencyChange && dependenciesChanged) {
      const defaultValue = getDefaultValue();
      const params = getParams(defaultValue);
      
      updateFilter(filterKey, defaultValue, params, persistAll);
      hasInitializedRef.current = false;
      lastDependenciesRef.current = [...dependencies];
    }
  }, [dependencies, resetOnDependencyChange, dependenciesChanged, getDefaultValue, getParams, updateFilter, filterKey, persistAll]);

  // Main initialization logic
  useEffect(() => {
    const shouldInitialize = !hasInitializedRef.current && 
                           (!waitForData || dataSource.length > 0) && 
                           !filterValues[filterKey];

    if (shouldInitialize) {
      let selectedValue;
      
      // Special handling for chips filter - chips don't need dataSource searching
      if (filterKey === 'chips') {
        // For chips, use initialValues directly (URL/localStorage chips) or default
        selectedValue = initialValues[filterKey] || getDefaultValue();
      } else {
        // For other filters, use the normal getInitialFilterValue logic
        const defaultValue = getDefaultValue();

        if (multiSelect) {
          // For multi-select filters (use arrays internally)
          const storedValue = initialValues[filterKey];
          if (Array.isArray(storedValue)) {
            // URL had multiple parameters - find matching items
            const matchedItems = storedValue
              .map(id => findItemInDataSource(dataSource, id))
              .filter(item => item !== undefined);
            selectedValue = matchedItems.length > 0 ? matchedItems : defaultValue;
          } else if (storedValue) {
            // Single URL parameter - find the item
            const foundItem = findItemInDataSource(dataSource, storedValue);
            selectedValue = foundItem ? [foundItem] : defaultValue;
          } else {
            selectedValue = defaultValue;
          }
        } else {
          // For traditional single-select filters (existing behavior)
          const defaultValue = getDefaultValue();
          selectedValue = getInitialFilterValue(filterKey, dataSource, defaultValue, findItemInDataSource);
        }
      }
      
      // Let the filter's getParams function handle the value appropriately
      const params = getParams(selectedValue);
      
      updateFilter(filterKey, selectedValue, params, persistAll);
      hasInitializedRef.current = true;
      lastDependenciesRef.current = [...dependencies];
      
      // Clear URL value flag after successful initialization
      hasUrlValueRef.current = false;
    }
  }, [
    filterKey,
    dataSource,
    filterValues,
    getInitialFilterValue,
    getDefaultValue,
    getParams,
    updateFilter,
    waitForData,
    findItemInDataSource,
    dependencies,
    storageKey,
    siteAppId,
    persistAll
  ]);

  return {
    isInitialized: hasInitializedRef.current,
    reinitialize: () => {
      hasInitializedRef.current = false;
    }
  };
}; 
